import { csrfHeader } from './csrf';
import { trackEvent } from './analytics';

export interface AuthUser {
  id: string;
  email?: string;
  name?: string;
  picture?: string;
  isAdmin?: boolean;
}

interface AuthSessionResponse {
  user?: AuthUser;
}

interface InternalReviewLoginResponse extends AuthSessionResponse {
  success?: boolean;
}

export async function getAuthSession(): Promise<AuthSessionResponse | null> {
  const response = await fetch('/api/auth/me', {
    method: 'GET',
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeader()
    },
    credentials: 'include'
  });

  if (!response.ok) {
    return null;
  }

  const payload = await response.json() as AuthSessionResponse;
  return payload?.user ? payload : null;
}

export function buildGoogleAuthUrl(returnTo?: string): string {
  if (typeof window === 'undefined') {
    return '/api/auth/google/start';
  }

  const url = new URL('/api/auth/google/start', window.location.origin);
  if (returnTo) {
    url.searchParams.set('returnTo', returnTo);
  }
  return url.toString();
}

export function isGoogleOAuthBlockedUserAgent(userAgent?: string): boolean {
  const ua = userAgent ?? (typeof navigator === 'undefined' ? '' : navigator.userAgent);
  if (!ua) return false;

  return /KAKAOTALK|KAKAOSTORY|Instagram|FBAN|FBAV|Line\/|NAVER|DaumApps|; wv\)|\bwv\b/i.test(ua);
}

function buildAndroidExternalBrowserIntent(targetUrl: string): string | null {
  if (typeof navigator === 'undefined' || !/Android/i.test(navigator.userAgent)) {
    return null;
  }

  let parsed: URL;
  try {
    parsed = new URL(targetUrl);
  } catch {
    return null;
  }

  if (parsed.protocol !== 'https:') {
    return null;
  }

  const pathAndQuery = `${parsed.host}${parsed.pathname}${parsed.search}`;
  const fallbackUrl = encodeURIComponent(targetUrl);
  return `intent://${pathAndQuery}#Intent;scheme=https;package=com.android.chrome;S.browser_fallback_url=${fallbackUrl};end`;
}

export function startGoogleLogin(returnTo?: string): void {
  if (typeof window === 'undefined') {
    return;
  }

  const authUrl = buildGoogleAuthUrl(returnTo);
  if (!isGoogleOAuthBlockedUserAgent()) {
    window.location.href = authUrl;
    return;
  }

  trackEvent('login_blocked_in_app_browser', {
    return_to: returnTo ?? '',
    user_agent_family: navigator.userAgent.slice(0, 80),
  });

  const externalUrl = buildAndroidExternalBrowserIntent(authUrl);
  const message = [
    '카카오톡/인스타그램 같은 앱 안에서는 Google 로그인이 차단될 수 있습니다.',
    '외부 브라우저에서 묘안 타로를 열면 정상적으로 로그인할 수 있습니다.',
  ].join('\n');

  if (externalUrl && window.confirm(`${message}\n\nChrome으로 열까요?`)) {
    window.location.href = externalUrl;
    return;
  }

  window.alert(`${message}\n\n오른쪽 위 메뉴에서 "외부 브라우저로 열기"를 선택한 뒤 다시 로그인해 주세요.`);
}

export async function logoutAuthSession(): Promise<boolean> {
  const response = await fetch('/api/auth/logout', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeader()
    },
    credentials: 'include'
  });

  return response.ok;
}

export async function loginInternalReview(
  email: string,
  password: string
): Promise<InternalReviewLoginResponse> {
  const response = await fetch('/api/auth/internal/login', {
    method: 'POST',
    headers: {
      'Content-Type': 'application/json',
      ...csrfHeader()
    },
    credentials: 'include',
    body: JSON.stringify({ email, password })
  });

  if (!response.ok) {
    let message = '내부 리뷰 계정 로그인에 실패했습니다.';
    try {
      const payload = await response.json() as { error?: { message?: string } };
      message = payload.error?.message || message;
    } catch {
      // ignore parse failure
    }
    throw new Error(message);
  }

  return response.json() as Promise<InternalReviewLoginResponse>;
}
